<template>
  <section class="historia-frenkel">
    <div class="historia-container">
      <div class="historia-image-block" data-aos="fade-right" data-aos-duration="1000">
        <h2 class="historia-title">{{ historia.title[selectedLang] }}</h2>
        <img
          class="historia-image"
          :src="imageSrc"
          alt="Historia Frenkel"
        />
        <p class="historia-caption">
          {{ captionText[selectedLang] }}
        </p>
      </div>
      <div class="historia-content-block" data-aos="fade-left" data-aos-duration="1000">
        <p
          class="historia-content underline-hover"
          v-html="historia.content[selectedLang]"
        ></p>
      </div>
    </div>
  </section>
</template>

<script>
import { historiaFrenkel } from '@/assets/data/data.ts'
import historiaImg from '@/assets/images/historia.png'
import AOS from 'aos'
import 'aos/dist/aos.css'

export default {
  name: 'HistoriaFrenkel',
  props: {
    selectedLang: {
      type: String,
      default: 'es'
    }
  },
  data() {
    return {
      historia: historiaFrenkel,
      imageSrc: historiaImg,
      captionText: {
        es: 'Observamos cada reto de nuestros clientes con atención y empatía, creando estrategias personalizadas para llevar su empresa al siguiente nivel.',
        en: 'We observe each client\'s challenge with care and empathy, creating tailored strategies to take their business to the next level.',
        de: 'Wir betrachten jede Herausforderung unserer Kunden aufmerksam und einfühlsam und entwickeln individuelle Strategien, um ihr Unternehmen auf die nächste Stufe zu bringen.'
      }
    }
  },
  mounted() {
    AOS.init({
      duration: 800,
      once: true
    });
    setTimeout(() => AOS.refresh(), 300);
  }
}
</script>

<style scoped>
/* Base y layout */
*,
*::before,
*::after {
  box-sizing: border-box;
}

.historia-frenkel {
  width: 100%;                  /* antes 100vw -> causaba overflow */
  background: #f5faf9;
  display: flex;
  justify-content: center;
  padding: 0;
  overflow-x: hidden;           /* cinturón de seguridad ante transform/hover */
}

.historia-container {
  width: 100%;
  max-width: 1200px;
  margin: 48px auto 0 auto;
  padding: 0 24px;              /* padding interno en lugar de 100vw */
  display: flex;
  flex-direction: row;
  gap: 48px;
  align-items: flex-start;
}

/* Columnas */
.historia-image-block {
  flex: 1 1 45%;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  min-width: 0;                 /* permite que en sm/md no fuerce overflow */
}

.historia-content-block {
  flex: 2 1 55%;
  min-width: 0;                 /* evita overflow por contenido largo */
  font-size: 1.08rem;
  color: #333;
  line-height: 1.6;
  display: flex;
  align-items: center;
}

/* Título */
.historia-title {
  font-family: 'Caveat-Bold', cursive;
  font-size: 2.5rem;
  font-weight: 400;
  margin-bottom: 18px;
  margin-top: 0;
}

/* Imagen */
.historia-image {
  display: block;
  width: 100%;
  height: auto;
  aspect-ratio: 1 / 1;
  object-fit: cover;
  border: 2px solid #222;
  background: #fff;
  margin-bottom: 8px;
  transition: transform 0.4s ease, box-shadow 0.4s ease;
}

/* Hover solo en pantallas medianas/grandes para no “empujar” el layout móvil */
@media (min-width: 768px) {
  .historia-image:hover {
    transform: scale(1.03);
    box-shadow: 0 10px 20px rgba(0, 0, 0, 0.25);
    cursor: pointer;
  }
}

.founder-bg-main:hover {
  transform: scale(1.05);
  box-shadow: 0 10px 20px rgba(0, 0, 0, 0.3);
  cursor: pointer;
  opacity: none !important;
}

/* Pie de foto */
.historia-caption {
  font-size: 0.9rem;
  color: #444;
  text-align: justify;
  margin-bottom: 8px;
  width: 100%;
}

/* Contenido (v-html) — romper palabras y evitar overflow */
.historia-content {
  white-space: pre-line;
  word-break: break-word;
  overflow-wrap: anywhere;
}

/* Subrayado animado sutil para título y párrafos */
.historia-title,
.historia-caption,
.historia-content p {
  position: relative;
  display: inline-block;
  cursor: default;
  transition: color 0.3s ease;
}

.historia-title::after,
.historia-content p::after {
  content: '';
  position: absolute;
  left: 0;
  bottom: 0;
  height: 2px;
  width: 0%;
  background-color: currentColor;
  transition: width 0.3s ease;
}

.historia-title:hover::after,
.historia-caption:hover::after,
.historia-content p:hover::after {
  width: 100%;
}

.underline-hover {
  position: relative;
  transition: color 0.3s ease;
}

.underline-hover::after {
  content: '';
  position: absolute;
  left: 0;
  bottom: -15px;
  width: 0%;
  height: 3px;
  background-color: #222;
  transition: width 0.4s ease;
  opacity: .2;
}

.underline-hover:hover::after {
  width: 100%;
  opacity: .2;
}

/* ===========================
   Responsive (md y sm)
   =========================== */

/* md: apilado en columna, tipografías ajustadas, sin desbordes */
@media (max-width: 992px) {
  .historia-container {
    flex-direction: column;
    gap: 28px;
    align-items: stretch;
  }
  .historia-image-block {
    align-items: center;
  }
  .historia-title {
    align-self: flex-start;
    text-align: justify;
    font-size: 2.2rem;
  }
  .historia-content-block {
    font-size: 1.02rem;
  }
}

/* sm: padding seguro, nada de 100vw, textos legibles */
@media (max-width: 576.98px) {
  .historia-container {
    width: 100%;
    padding: 0 16px;            /* antes 100vw -> provocaba scroll horizontal */
    margin-top: 32px;
    gap: 22px;
  }
  .historia-title {
    font-size: 1.9rem;
    margin-bottom: 12px;
  }
  .historia-content-block {
    font-size: 0.98rem;
    line-height: 1.65;
  }
  .historia-caption {
    font-size: 0.82rem;
  }
}
</style>
